import React, { useState, useEffect } from 'react';
import { Dropdown } from '../../helpers/Dropdown';
import CustomDatePicker from '../../helpers/CustomDatePicker_V2';
import { updateClient } from '../../../services/clientService';
import {
    fetchSICCodes,
    fetchTenantTiers,
    fetchClientTypes,
    fetchPopulationRanges,
    getAgency,
} from '../../../services/systemService';
import Toast from '../../ui/toasts/Toast';
import style from '../ClientList.module.scss';
import { formatZipCode } from '../../../utils/utils';
import AgencyDropdown from '../../shared/asyncDropdowns/AgencyDropdown';
import AgencyOfficeDropdown from '../../shared/asyncDropdowns/AgencyOfficeDropdown';
import UsStatesTerritories from '../../shared/asyncDropdowns/UsStatesTerritories';
import { useAuthContext } from '../../../store/AuthContext';
import { SecurityAttributes } from '../../../utils/data/enums';
import { getText } from '../../../i18n';

const ClientDetailsForm = ({ initialData = {}, onSave }) => {
    const [recordData, setRecordData] = useState({
        ...initialData,
    });

    const [SICCodes, setSICCodes] = useState([]);
    const [baseTiers, setBaseTiers] = useState([]);
    const [filteredTiers, setFilteredTiers] = useState([]);
    const [clientTypes, setClientTypes] = useState([]);
    const [populationRanges, setPopulationRanges] = useState([]);
    const [addFolder, setAddFolder] = useState([]);
    const { hasSecurityAttribute } = useAuthContext();

    useEffect(() => {
        // TODO: Add US territories dropdown

        getSICCodes();
        getTiers();
        getClientTypes();
        getPopulationRanges();
    }, []);

    const getSICCodes = async () => {
        try {
            const data = await fetchSICCodes();
            const transformedArray = transformArray(data);

            transformedArray.unshift({ value: null, label: getText('msg_6c4c2af36393') });
            setSICCodes(transformedArray);
        } catch (error) {
            console.error('Could not fetch:', error);
        }
    };

    const getTiers = async () => {
        try {
            const data = await fetchTenantTiers();
            const transformedArray = transformArray(data);
            setBaseTiers(transformedArray);
        } catch (error) {
            console.error('Could not fetch:', error);
        }
    };

    const getClientTypes = async () => {
        try {
            const data = await fetchClientTypes();
            const userSelectableClientTypes = data.filter(
                ct => ct.isUserSelectable,
            );
            const transformedArray = transformArray(userSelectableClientTypes);
            transformedArray.sort((a, b) =>
                (a.label || '').localeCompare(b.label || '', undefined, {
                    sensitivity: 'base',
                }),
            );
            setClientTypes(transformedArray);
        } catch (error) {
            console.error('Could not fetch:', error);
        }
    };

    const getPopulationRanges = async () => {
        try {
            const data = await fetchPopulationRanges();
            const transformedArray = transformArray(data);
            setPopulationRanges(transformedArray);
        } catch (error) {
            console.error('Could not fetch:', error);
        }
    };

    const handleInputChange = event => {
        const { name, value } = event.target;

        if (name.startsWith('address')) {
            // Split the name to get the specific field (e.g., 'address', 'address2', etc.)
            const field = name.split('.')[1];
            setRecordData(prevData => ({
                ...prevData,
                address: {
                    ...prevData.address,
                    [field]: value,
                },
            }));
        } else {
            setRecordData(prevData => ({
                ...prevData,
                [name]: value,
            }));
        }
    };

    const handleDropdownChange = async (name, value) => {
        if (name === 'agencyId') {
            const agencyData = await getAgency(value);
            if (agencyData?.tenantTiers.length > 0) {
                const filteredTiers = baseTiers.filter(tier =>
                    agencyData.tenantTiers.includes(tier.value),
                );
                setFilteredTiers(filteredTiers);
                if (
                    recordData.tenantTierId &&
                    !agencyData.tenantTiers.includes(recordData.tenantTierId)
                ) {
                    setRecordData(prevData => ({
                        ...prevData,
                        tenantTierId: null,
                    }));
                }
            }
            setRecordData(prevData => ({
                ...prevData,
                ['agencyOfficeId']: null,
            }));
        }
        setRecordData(prevData => ({
            ...prevData,
            [name]: value,
        }));
    };

    const handleDateChange = (field, date) => {
        setRecordData(prevData => ({
            ...prevData,
            [field]: date,
        }));
    };

    const transformArray = data => {
        return data.map(item => ({
            value: item.id,
            label: item.name,
        }));
    };

    const getDefaultSICCodeLabel = () => {
        const matchingCode = SICCodes.find(
            item => item.value === recordData.sicCodeId,
        );
        return matchingCode ? matchingCode.label : 'Select';
    };

    const getDefaultTierLabel = () => {
        const matchingTier = baseTiers.find(
            item => item.value === recordData.tenantTierId,
        );
        return matchingTier ? matchingTier.label : 'Select';
    };

    const getDefaultClientTypeLabel = () => {
        const matchingClientType = clientTypes.find(
            item => item.value === recordData.tenantStatusId,
        );
        return matchingClientType ? matchingClientType.label : 'Select';
    };

    const getDefaultPopulationRangeLabel = () => {
        const matchingPopulationRange = populationRanges.find(
            item => item.value === recordData.populationRangeId,
        );
        return matchingPopulationRange
            ? matchingPopulationRange.label
            : 'Select';
    };

    const handleSave = () => {
        updateClient(recordData)
            .then(() => {
                Toast.success(
                    'Client ' + recordData.name + ' was updated successfully.',
                );
            })
            // this will refresh the client details tab with the updated info by re-fetching the client record
            .then(onSave)
            .catch(error => {
                console.error('Unable to save changes', error);
                if (error?.response?.data?.detail) {
                    Toast.error(error?.response?.data?.detail);
                } else
                    Toast.error(
                        getText('msg_9e74a5a82bcc'),
                    );
            });
    };

    const handelFolderAdd = () => {
        if (!addFolder) return;

        if (recordData.folders) {
            const folders = recordData.folders;

            const duplicateFolder = folders.filter(function (folder) {
                return folder.toLowerCase() === addFolder.toLowerCase();
            });

            if (duplicateFolder.length > 0) {
                Toast.error(getText('msg_f25ffbfe53e6'));
                return;
            }

            folders.push(addFolder);
            setRecordData(prevData => ({ ...prevData, folders }));
        } else
            setRecordData(prevData => ({ ...prevData, folders: [addFolder] }));

        setAddFolder('');
    };

    const removeValue = folderName => {
        const folders = [...recordData.folders].filter(function (folder) {
            return folder !== folderName;
        });

        setRecordData(prevData => ({ ...prevData, folders }));
    };

    return (
        <form className={style.demographicFormContainer}>
            <div className={`row px-5 pb-5`}>
                <div className={`col-12 col-xs-12 col-md-12 col-lg-6`}>
                    <div
                        className={`form-group mb-3 inline-input flex-sm-wrap flex-lg-nowrap`}
                    >
                        <label htmlFor="clientCode">
                            {getText('msg_6346915c34e2')} <span className="text-danger">*</span>
                        </label>
                        <input
                            id="clientCode"
                            type="text"
                            maxLength="50"
                            className="form-control"
                            value={recordData.code || ''}
                            onChange={handleInputChange}
                            name="code"
                            required
                        />
                    </div>
                    <div
                        className={`form-group mb-3 inline-input flex-sm-wrap flex-lg-nowrap`}
                    >
                        <label htmlFor="clientName">
                            {getText('msg_12e281345750')} <span className="text-danger">*</span>
                        </label>
                        <input
                            id="clientName"
                            type="text"
                            maxLength="50"
                            className="form-control"
                            value={recordData.name || ''}
                            onChange={handleInputChange}
                            name="name"
                            required
                        />
                    </div>
                    <div
                        className={`form-group mb-3 inline-input flex-sm-wrap flex-lg-nowrap`}
                    >
                        <label htmlFor="preferredClientName">
                            {getText('msg_6df7d8380ffc')}
                        </label>
                        <input
                            id="preferredClientName"
                            type="text"
                            maxLength="50"
                            className="form-control"
                            value={recordData.preferredName || ''}
                            onChange={handleInputChange}
                            name="preferredName"
                        />
                    </div>
                    <div
                        className={`form-group mb-3 inline-input flex-sm-wrap flex-lg-nowrap`}
                    >
                        <label htmlFor="sicCode">{getText('msg_ae5e55841c75')}</label>
                        <Dropdown
                            defaultValue={getDefaultSICCodeLabel()}
                            options={SICCodes}
                            onChange={value =>
                                setRecordData(prevData => ({
                                    ...prevData,
                                    sicCodeId: value,
                                }))
                            }
                        />
                    </div>
                    <div
                        className={`form-group mb-3 inline-input flex-sm-wrap flex-lg-nowrap`}
                    >
                        <label htmlFor="clientExpirationDate">
                            {getText('msg_29a152dbe5f2')}
                        </label>
                        <CustomDatePicker
                            selectedDate={recordData.expirationDate}
                            onDateChange={date =>
                                handleDateChange('expirationDate', date)
                            }
                        />
                    </div>
                    <div
                        className={`form-group mb-3 inline-input flex-sm-wrap flex-lg-nowrap`}
                    >
                        <AgencyDropdown
                            selectedAgency={recordData.agencyId}
                            onAgencyChange={agencyId =>
                                handleDropdownChange('agencyId', agencyId)
                            }
                            inlineLabel={true}
                            allowNullSelection={true}
                            isRequired
                        />
                    </div>
                    <div
                        className={`form-group mb-3 inline-input flex-sm-wrap flex-lg-nowrap`}
                    >
                        <AgencyOfficeDropdown
                            selectedOffice={recordData.agencyOfficeId}
                            agencyId={recordData.agencyId}
                            onOfficeChange={officeId =>
                                handleDropdownChange('agencyOfficeId', officeId)
                            }
                            inlineLabel={true}
                        />
                    </div>
                    <div
                        className={`form-group mb-3 inline-input flex-sm-wrap flex-lg-nowrap`}
                    >
                        <label htmlFor="tier">
                            {getText('msg_5bd44ebe63cd')} <span className="text-danger">*</span>
                        </label>
                        <Dropdown
                            defaultValue={getDefaultTierLabel()}
                            options={
                                filteredTiers.length > 0
                                    ? filteredTiers
                                    : baseTiers
                            }
                            onChange={value =>
                                setRecordData(prevData => ({
                                    ...prevData,
                                    tenantTierId: value,
                                }))
                            }
                        />
                    </div>
                    <div
                        className={`form-group mb-3 inline-input flex-sm-wrap flex-lg-nowrap`}
                    >
                        <label htmlFor="clientType">
                            {getText('msg_bae7d5be7082')} <span className="text-danger">*</span>
                        </label>
                        <Dropdown
                            defaultValue={getDefaultClientTypeLabel()}
                            options={clientTypes}
                            onChange={value =>
                                setRecordData(prevData => ({
                                    ...prevData,
                                    tenantStatusId: value,
                                }))
                            }
                        />
                    </div>
                </div>
                <div className="col-12 col-xs-12 col-md-12 col-lg-6 align-self-baseline">
                    <div
                        className={`form-group mb-3 inline-input flex-sm-wrap flex-lg-nowrap`}
                    >
                        <label htmlFor="address">{getText('msg_d70f93df5e8f')}</label>
                        <input
                            id="address"
                            type="text"
                            maxLength="50"
                            className="form-control"
                            value={recordData.address?.address || ''}
                            onChange={handleInputChange}
                            name="address.address"
                            required
                        />
                    </div>
                    <div
                        className={`form-group mb-3 inline-input flex-sm-wrap flex-lg-nowrap`}
                    >
                        <label htmlFor="address2">{getText('msg_62bcfe5f085f')}</label>
                        <input
                            id="address2"
                            type="text"
                            maxLength="50"
                            className="form-control"
                            value={recordData.address?.address2 || ''}
                            onChange={handleInputChange}
                            name="address.address2"
                        />
                    </div>
                    <div
                        className={`form-group mb-3 inline-input flex-sm-wrap flex-lg-nowrap`}
                    >
                        <label htmlFor="city">{getText('msg_4271627f4f0b')}</label>
                        <input
                            id="city"
                            type="text"
                            maxLength="50"
                            className="form-control"
                            value={recordData.address?.city || ''}
                            onChange={handleInputChange}
                            name="address.city"
                            required
                        />
                    </div>
                    <div
                        className={`form-group mb-3 inline-input flex-sm-wrap flex-lg-nowrap`}
                    >
                        <UsStatesTerritories
                            selectedState={recordData.address?.state || ''}
                            onStateChange={state =>
                                setRecordData(prevData => ({
                                    ...prevData,
                                    address: {
                                        ...prevData.address,
                                        state: state,
                                    },
                                }))
                            }
                            isRequired={false}
                            inlineLabel={true}
                        />
                    </div>
                    <div
                        className={`form-group mb-3 inline-input flex-sm-wrap flex-lg-nowrap`}
                    >
                        <label htmlFor="zip">{getText('msg_d9d220ab2925')}</label>
                        <input
                            id="zip"
                            type="text"
                            maxLength="10"
                            className="form-control"
                            value={recordData.address?.zip || ''}
                            onChange={e => {
                                const { name } = e.target;
                                const field = name.split('.')[1];
                                setRecordData(prevData => ({
                                    ...prevData,
                                    address: {
                                        ...prevData.address,
                                        [field]: formatZipCode(e.target.value),
                                    },
                                }));
                            }}
                            name="address.zip"
                            required
                        />
                    </div>

                    <div
                        className={`form-group mb-3 inline-input flex-sm-wrap flex-lg-nowrap`}
                    >
                        <label htmlFor="folderAdd">{getText('msg_cde4dd6baaf4')}</label>
                        <input
                            id="folder"
                            type="text"
                            maxLength="255"
                            className="form-control"
                            value={addFolder}
                            onChange={e => {
                                setAddFolder(e.target.value);
                            }}
                            name="folder"
                        />
                        <button
                            onClick={handelFolderAdd}
                            type="button"
                            className="ms-3 button-base ing-button-primary-left align-middle"
                        >
                            {getText('msg_61cc55aa0453')}
                        </button>
                    </div>
                    <div
                        className={`form-group mb-3 inline-input flex-sm-wrap flex-lg-nowrap`}
                    >
                        <label htmlFor="folders"></label>
                        <div
                            style={{
                                overflow: 'auto',
                                borderRadius: '12px',
                                border: '1px solid var(--alpha-black-20, rgba(47, 43, 67, 0.2))',
                                boxShadow:
                                    '0px -1px 0px 0px rgba(47, 43, 67, 0.1) inset, 0px 1px 3px 0px rgba(47, 43, 67, 0.1)',
                            }}
                        >
                            <div
                                className="form-control"
                                placeholder={getText('msg_79dd2019667a')}
                                aria-label={getText('msg_82410b800401')}
                                style={{
                                    display: 'flex',
                                    flexWrap: 'wrap',
                                    alignItems: 'center',
                                    border: 'unset',
                                    minHeight: '45px',
                                    resize: 'none',
                                }}
                            >
                                {recordData.folders &&
                                    recordData.folders.length > 0
                                    ? recordData.folders.map((item, ind) => (
                                        <button
                                            key={ind}
                                            className="ing-button-tag"
                                            style={{ margin: '4px 4px' }}
                                            onClick={e => {
                                                e.preventDefault();
                                                removeValue(item);
                                            }}
                                        >
                                            <i className="bi bi-x-lg"></i>
                                            {item}
                                        </button>
                                    ))
                                    : ''}
                            </div>
                        </div>
                    </div>
                    <div
                        className={`form-group mb-3 inline-input flex-sm-wrap flex-lg-nowrap`}
                    >
                        <label>{getText('msg_2e8a57cc5c47')}</label>
                        <input
                            id="website"
                            type="text"
                            maxLength="50"
                            className="form-control"
                            value={recordData?.website || ''}
                            onChange={handleInputChange}
                            name="website"
                        />
                    </div>
                </div>
                <div
                    className="d-flex justify-content-between mb-5"
                    style={{ marginTop: '60px' }}
                >
                    <p>
                        <span className="text-danger">*</span> {getText('msg_7c118f042eaa')}
                    </p>
                    {(hasSecurityAttribute([SecurityAttributes.ClientFullAccess])) && (
                        <button
                            onClick={handleSave}
                            type="button"
                            className="ms-3 button-base ing-button-primary-left align-middle"
                        >
                            <i className="bi bi-check-lg me-2" />
                            {getText('msg_efc007a393f6')}
                        </button>
                    )}
                </div>
            </div>
        </form>
    );
};

export default ClientDetailsForm;
